// src/contexts/GamePlayContext.js - 세션 점수 누적 시스템 + sessionId 동기화 수정

import React, { createContext, useState, useContext, useCallback } from 'react';
import { getSubscriptionBenefits } from '../config';
import { debugLog } from '../utils/environment';

const GamePlayContext = createContext();

export const useGamePlayContext = () => {
  const context = useContext(GamePlayContext);
  if (!context) {
    throw new Error('useGamePlayContext must be used within GamePlayProvider');
  }
  return context;
};

export const GamePlayProvider = ({ children }) => {
  // 🔧 기존 게임 플레이 관련 상태 (유지)
  const [playMode, setPlayMode] = useState('theme');
  const [remainingHints, setRemainingHints] = useState(3);
  const [usedHints, setUsedHints] = useState(0);
  const [perfectStages, setPerfectStages] = useState([]);
  
  // 🔧 기존 세션 단위 광고 시청 상태 관리 (유지)
  const [hasWatchedHintAdInSession, setHasWatchedHintAdInSession] = useState(false);
  
  // 🆕 새로 추가: 세션 점수 누적 시스템
  const [sessionTotalScore, setSessionTotalScore] = useState(0);
  const [sessionStageScores, setSessionStageScores] = useState([]);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  
  // 🆕 새로 추가: 각 스테이지 점수를 세션에 누적하는 함수
  const addStageScore = useCallback((stageScore) => {
    // 입력값 검증
    if (typeof stageScore !== 'number' || stageScore < 0) {
      debugLog('[GamePlayContext] 잘못된 점수 값:', stageScore);
      return false;
    }
    
    // 세션 총합에 추가
    setSessionTotalScore(prev => {
      const newTotal = prev + stageScore;
      debugLog('[GamePlayContext] 스테이지 점수 누적:', {
        stageScore,
        previousTotal: prev,
        newTotal,
        sessionId
      });
      return newTotal;
    });
    
    // 스테이지별 점수 기록 (디버깅용)
    setSessionStageScores(prev => {
      const newScores = [...prev, stageScore];
      // 배열 크기 제한 (메모리 보호)
      if (newScores.length > 15) {
        newScores.splice(0, newScores.length - 15);
      }
      return newScores;
    });
    
    return true;
  }, [sessionId]);
  
  // 🆕 새로 추가: 현재 세션의 총 점수를 반환하는 함수
  const getSessionTotalScore = useCallback(() => {
    debugLog('[GamePlayContext] 세션 총 점수 조회:', {
      sessionTotalScore,
      stageCount: sessionStageScores.length,
      sessionId
    });
    return sessionTotalScore;
  }, [sessionTotalScore, sessionStageScores.length, sessionId]);
  
  // 🆕 새로 추가: 세션 데이터를 초기화하는 함수
  const resetSession = useCallback(() => {
    debugLog('[GamePlayContext] 세션 초기화 실행');
    setSessionTotalScore(0);
    setSessionStageScores([]);
    setSessionStartTime(null);
    setSessionId(null);
  }, []);
  
  // 🆕 새로 추가: 새로운 게임 세션을 시작하는 함수
  const startGameSession = useCallback((mode, themeId, stageIdList) => {
    // 고유한 세션 ID 생성
    const newSessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    debugLog('[GamePlayContext] 새 게임 세션 시작:', {
      sessionId: newSessionId,
      mode,
      themeId,
      stageCount: stageIdList?.length || 'unknown'
    });
    
    // 세션 정보 설정
    setSessionId(newSessionId);
    setSessionStartTime(Date.now());
    setPlayMode(mode);
    
    // 기존 세션 데이터 초기화
    resetSession();
    
    return newSessionId;
  }, [resetSession]);
  
  // 🔧 기존 함수 수정: 새 게임 시작 - 구독 정보는 AppContext에서 가져와야 함
  const startNewGame = useCallback((mode, subscriptionStatus) => {
    setPlayMode(mode);
    
    // 🆕 새로 추가: 새 게임 시작 시 새로운 sessionId 생성
    const newSessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
    
    // 🔧 기존 로직 유지: 구독 혜택에 따른 추가 힌트
    const baseHints = mode === 'random' ? 5 : (mode === 'ranking' ? 0 : 3);
    const benefits = subscriptionStatus ? getSubscriptionBenefits(subscriptionStatus) : null;
    const bonusHints = benefits?.bonusHints || 0;
    setRemainingHints(baseHints + bonusHints);
    setUsedHints(0);
    setPerfectStages([]);
    
    // 🔧 기존 로직 유지: 세션 시작 시 광고 시청 상태 초기화
    setHasWatchedHintAdInSession(false);
    
    // 🆕 새로 추가: 세션 초기화 (점수 누적 시스템)
    setSessionTotalScore(0);
    setSessionStageScores([]);
    setSessionStartTime(Date.now());
    
    debugLog('[GamePlayContext] 새 게임 시작:', {
      mode,
      baseHints,
      bonusHints,
      totalHints: baseHints + bonusHints,
      newSessionId  // 🆕 추가: 새 세션 ID 로그
    });
  }, []);
  
  // 🔧 기존 함수 유지: 힌트 소비
  const consumeHint = useCallback(() => {
    if (remainingHints > 0) {
      setRemainingHints(prev => prev - 1);
      setUsedHints(prev => prev + 1);
      debugLog('[GamePlayContext] 힌트 사용 - 남은 힌트:', remainingHints - 1);
      return true;
    }
    return false;
  }, [remainingHints]);
  
  // 🔧 기존 함수 유지: 힌트 추가 함수 - 광고 시청 후 호출
  const addHints = useCallback((count) => {
    debugLog('[GamePlayContext] 힌트 추가:', count);
    setRemainingHints(prev => prev + count);
  }, []);
  
  // 🔧 기존 함수 유지: 퍼펙트 스테이지 추가
  const addPerfectStage = useCallback((stageId) => {
    setPerfectStages(prev => [...prev, stageId]);
    debugLog('[GamePlayContext] 퍼펙트 스테이지 추가:', stageId);
  }, []);
  
  const value = {
    // 🔧 기존 상태 유지
    playMode,
    remainingHints,
    usedHints,
    perfectStages,
    hasWatchedHintAdInSession,
    
    // 🆕 새로 추가: 세션 점수 관련 상태
    sessionTotalScore,
    sessionStageScores,
    sessionId,  // 🔧 수정: sessionId를 value에 추가하여 외부에서 접근 가능
    
    // 🔧 기존 함수 유지
    startNewGame,
    consumeHint,
    addHints,
    addPerfectStage,
    setHasWatchedHintAdInSession,
    
    // 🆕 새로 추가: 세션 점수 관련 함수
    addStageScore,
    getSessionTotalScore,
    resetSession,
    startGameSession
  };
  
  return (
    <GamePlayContext.Provider value={value}>
      {children}
    </GamePlayContext.Provider>
  );
};